// Cart transform drops the bundle parent's line item properties, and notification Liquid
// cannot read line_item_group attributes — cart attributes are the only carrier the order
// confirmation email can read that also survives Shop Pay.
// Format: records joined by |~B~|, each = variant id then key|~KV~|value pairs joined by |~P~|.
(() => {
  const RECORD_DELIM = '|~B~|';
  const PAIR_DELIM = '|~P~|';
  const KV_DELIM = '|~KV~|';
  const ATTRIBUTE_KEY = '_bundle_personalisation';
  const OPTION_KEYS_DELIM = '|~K~|';
  // Tells the email this record already had its option keys excluded by key, so it must not fall
  // back to guessing them from values. strip() keeps a customer from forging it.
  const KEYED_MARKER = '_k' + KV_DELIM + '1';
  const MAX_LENGTH = 4000;
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?$/;
  const DEBOUNCE_MS = 150;

  const originalFetch = window.fetch.bind(window);

  let syncTimer = null;
  let syncing = false;
  let pending = false;
  let rerunRequested = false;

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function strip(value) {
    return String(value)
      .split(RECORD_DELIM)
      .join(' ')
      .split(PAIR_DELIM)
      .join(' ')
      .split(KV_DELIM)
      .join(' ');
  }

  function buildValue(cart) {
    const records = [];

    for (const item of cart.items || []) {
      const properties = item.properties || {};
      if (!properties._bundle_id) {
        continue;
      }
      // The group properties read "group name: selected product", which only repeats the child
      // lines the email already renders, so they are left out (engine-bundle.js names them).
      const optionKeys = new Set(
        String(properties._bundle_option_keys || '')
          .split(OPTION_KEYS_DELIM)
          .filter(Boolean)
      );
      // Emitted even when empty so same-variant bundles stay positionally aligned.
      const fields = [String(item.variant_id)];
      // Only a record whose option rows really were excluded may claim the marker. A bundle added
      // to the cart before engine-bundle.js declared its keys still carries the group properties,
      // and the email has to keep matching those off by value.
      if (optionKeys.size) {
        fields.push(KEYED_MARKER);
      }
      for (const key of Object.keys(properties)) {
        if (key.charAt(0) === '_' || optionKeys.has(key)) {
          continue;
        }
        const value = properties[key];
        if (value === '' || value === null || value === undefined) {
          continue;
        }
        fields.push(strip(key) + KV_DELIM + strip(value));
      }
      records.push(fields.join(PAIR_DELIM));
    }

    const total = records.length;
    let value = records.join(RECORD_DELIM);
    while (value.length > MAX_LENGTH && records.length) {
      records.pop();
      value = records.join(RECORD_DELIM);
    }
    if (records.length !== total) {
      console.warn(
        `[Bundle Personalisation] attribute truncated: ${total - records.length} of ${total} bundles dropped.`
      );
    }

    return value;
  }

  async function sync() {
    if (syncing) {
      rerunRequested = true;
      return;
    }
    syncing = true;
    try {
      const root = cartRoot();
      const cart = await originalFetch(`${root}cart.js`).then((response) => response.json());
      const next = buildValue(cart);
      const current = (cart.attributes || {})[ATTRIBUTE_KEY] || '';
      if (next === current) {
        pending = false;
        return;
      }
      await originalFetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ attributes: { [ATTRIBUTE_KEY]: next } }),
      });
      pending = false;
    } catch (error) {
      console.error('[Bundle Personalisation] cart attribute sync failed:', error);
    } finally {
      syncing = false;
      if (rerunRequested) {
        rerunRequested = false;
        schedule();
      }
    }
  }

  function schedule() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(sync, DEBOUNCE_MS);
    pending = true;
  }

  function flush() {
    if (!pending) {
      return;
    }
    clearTimeout(syncTimer);
    sync();
  }

  function requestUrl(input) {
    try {
      if (typeof input === 'string') {
        return input;
      }
      if (input instanceof URL) {
        return input.href;
      }
      return (input && input.url) || '';
    } catch (error) {
      return '';
    }
  }

  // No shared choke point for cart mutations across theme scripts, so trigger off the request.
  window.fetch = function patchedFetch(...args) {
    const result = originalFetch(...args);
    const url = requestUrl(args[0]).split('?')[0];
    if (CART_MUTATION.test(url)) {
      result
        .then((response) => {
          if (response && response.ok) {
            schedule();
          }
        })
        .catch(() => {});
    }
    return result;
  };

  window.syncBundlePersonalisation = schedule;

  if (typeof module === 'object' && module.exports) {
    module.exports = { buildValue, MAX_LENGTH, RECORD_DELIM, PAIR_DELIM, KV_DELIM, OPTION_KEYS_DELIM, KEYED_MARKER }; // node (tests)
  }

  // Accelerated checkout builds its order from the cart the moment it is pressed, so a
  // pending mirror has to land before the click rather than on the debounce.
  document.addEventListener(
    'pointerdown',
    (event) => {
      const target = event.target;
      if (
        target &&
        target.closest &&
        target.closest(
          '[name="checkout"], #checkout-submit, shopify-accelerated-checkout, shopify-accelerated-checkout-cart, .shopify-payment-button, [data-shopify="dynamic-checkout-cart"]'
        )
      ) {
        flush();
      }
    },
    true
  );

  // Safety net for carts mutated outside the patched fetch.
  if (document.cookie.split('; ').some((entry) => entry.startsWith('cart='))) {
    if (window.requestIdleCallback) {
      requestIdleCallback(schedule, { timeout: 3000 });
    } else {
      window.addEventListener('load', schedule);
    }
  }
})();
